'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';

interface Service {
  id: number;
  name: string;
  nameDe: string;
  description: string;
  descriptionDe: string;
  price: string;
  priceDe: string;
  image: string;
  hoverImage: string;
  alt: string;
  hoverAlt: string;
}

const servicesData: Service[] = [
  {
    id: 1,
    name: 'Vision Sprint – Strategic Blueprint & Product Vision',
    nameDe: 'Vision Sprint – Strategischer Entwurf & Produktvision',
    description: 'An intensive workshop driven engagement to define your product vision, target audience, growth markets and performance metrics. You\'ll get a vision document, a detailed roadmap and a KPI framework to guide your marketing and product development. The package includes two workshops and four weeks of hands-on support to ensure the strategy is aligned and implementation kicks off smoothly.',
    descriptionDe: 'Ein intensives Workshop-gesteuertes Engagement zur Definition Ihrer Produktvision, Zielgruppe, Wachstumsmärkte und Leistungskennzahlen. Sie erhalten ein Visionsdokument, eine detaillierte Roadmap und ein KPI-Framework zur Steuerung Ihres Marketings und Ihrer Produktentwicklung. Das Paket umfasst zwei Workshops und vier Wochen praktische Unterstützung, um sicherzustellen, dass die Strategie abgestimmt ist und die Umsetzung reibungslos beginnt.',
    price: '€3,600 one-time fee',
    priceDe: '€3.600 Einmalgebühr',
    image: '/assets/images/vision_sprint_strategic_planning_team.png',
    hoverImage: '/assets/images/vision_sprint_strategic_blueprint.png',
    alt: 'Professional business team collaborating on strategic planning and product vision in modern office',
    hoverAlt: 'Strategic blueprint document showing product vision roadmap and KPI framework'
  },
  {
    id: 2,
    name: 'Performance LaunchPad – Campaign Setup & Early Optimisation',
    nameDe: 'Performance LaunchPad – Kampagnen-Setup & Früh-Optimierung',
    description: 'A comprehensive setup for your growth campaigns on major social media and advertising platforms. We plan your media buying, design the campaign architecture, configure tracking and analytics, prepare creative briefings and build starter ad sets across the appropriate channels. Four weeks of optimisation are included to refine targeting, creatives, bids and daily budgets to generate meaningful data and give your campaigns a strong start.',
    descriptionDe: 'Ein umfassendes Setup für Ihre Wachstumskampagnen auf großen Social-Media- und Werbeplattformen. Wir planen Ihren Media-Einkauf, entwerfen die Kampagnenarchitektur, konfigurieren Tracking und Analytik, bereiten kreative Briefings vor und erstellen Starter-Anzeigensets über die entsprechenden Kanäle. Vier Wochen Optimierung sind enthalten, um Targeting, Creatives, Gebote und Tagesbudgets zu verfeinern, aussagekräftige Daten zu generieren und Ihren Kampagnen einen starken Start zu ermöglichen.',
    price: '€4,800 one-time fee',
    priceDe: '€4.800 Einmalgebühr',
    image: '/assets/images/performance_launchpad_campaign_team.png',
    hoverImage: '/assets/images/performance_launchpad_campaign.png',
    alt: 'Marketing professionals working on digital advertising campaigns with analytics dashboards',
    hoverAlt: 'Performance campaign dashboard showing ad metrics, targeting setup and optimisation results'
  },
  {
    id: 3,
    name: 'Growth Retainer – Ongoing Optimisation & Analytics',
    nameDe: 'Growth Retainer – Laufende Optimierung & Analytik',
    description: 'Monthly service for continuous improvement. We review your KPIs and analytics, optimise campaigns, refine creative assets and adjust strategy. Weekly touchpoints keep you informed and allow for rapid iteration.',
    descriptionDe: 'Monatlicher Service für kontinuierliche Verbesserung. Wir überprüfen Ihre KPIs und Analytik, optimieren Kampagnen, verfeinern kreative Assets und passen die Strategie an. Wöchentliche Kontaktpunkte halten Sie informiert und ermöglichen schnelle Iteration.',
    price: '€2,500 per month',
    priceDe: '€2.500 pro Monat',
    image: '/assets/images/growth_retainer_optimization_team.png',
    hoverImage: '/assets/images/growth_retainer_optimization.png',
    alt: 'Data analysts and marketing specialists reviewing growth metrics and KPI dashboards',
    hoverAlt: 'Growth retainer analytics dashboard showing KPI trends and campaign optimisation metrics'
  },
  {
    id: 4,
    name: 'AI Workflow Kickstart – AI Integration & Process Automation',
    nameDe: 'AI Workflow Kickstart – AI-Integration & Prozessautomatisierung',
    description: 'Turnkey solution to integrate AI into your marketing workflow. We define your AI strategy, streamline processes, deploy custom AI agents and set up an AI-driven toolchain and content calendar.',
    descriptionDe: 'Schlüsselfertige Lösung zur Integration von AI in Ihren Marketing-Workflow. Wir definieren Ihre AI-Strategie, optimieren Prozesse, setzen maßgeschneiderte AI-Agenten ein und richten eine AI-gesteuerte Toolchain und einen Content-Kalender ein.',
    price: '€6,200 one-time fee',
    priceDe: '€6.200 Einmalgebühr',
    image: '/assets/images/ai_workflow_kickstart_integration.png',
    hoverImage: '/assets/images/ai_workflow_kickstart.png',
    alt: 'Tech team implementing AI automation tools and workflow integration in innovative workspace',
    hoverAlt: 'AI workflow diagram showing automated marketing processes and custom AI agent deployment'
  },
  {
    id: 5,
    name: 'AI Content Engine – Monthly Content Production',
    nameDe: 'AI Content Engine – Monatliche Content-Produktion',
    description: 'Subscription package for professionally reviewed AI-generated content. Each month we deliver videos, custom image assets and written content that feeds your AEO, GEO and SEO growth strategies as well as your brand narratives and performance campaigns. Everything is organized by an editorial calendar which is adjusted based on feedback and analytics.',
    descriptionDe: 'Abo-Paket für professionell überprüfte KI-generierte Inhalte. Jeden Monat liefern wir Videos, benutzerdefinierte Bild-Assets und schriftliche Inhalte, die Ihre AEO-, GEO- und SEO-Wachstumsstrategien sowie Ihre Markennarrative und Performance-Kampagnen unterstützen. Alles wird durch einen redaktionellen Kalender organisiert, der basierend auf Feedback und Analytik angepasst wird.',
    price: '€3,200 per month',
    priceDe: '€3.200 pro Monat',
    image: '/assets/images/ai_content_engine_production_team.png',
    hoverImage: '/assets/images/ai_content_engine_production.png',
    alt: 'Content creators and AI specialists producing multimedia content in vibrant creative studio',
    hoverAlt: 'AI content engine production pipeline showing monthly content calendar and brand asset creation'
  },
  {
    id: 6,
    name: 'Web Presence Builder – Professional Website Creation',
    nameDe: 'Web Presence Builder – Professionelle Website-Erstellung',
    description: 'We design and build a polished, responsive website like planetpulsestudios.com for your brand. This includes UX/UI design, content structuring, performance optimisation and on-page SEO to ensure a modern, compelling web presence.',
    descriptionDe: 'Wir entwerfen und erstellen eine professionelle, responsive Website wie planetpulsestudios.com für Ihre Marke. Dies umfasst UX/UI-Design, Content-Strukturierung, Performance-Optimierung und On-Page-SEO für eine moderne, überzeugende Web-Präsenz.',
    price: '€5,000 one-time fee',
    priceDe: '€5.000 Einmalgebühr',
    image: '/assets/images/web_presence_builder_design_team.png',
    hoverImage: '/assets/images/web_presence_builder.png',
    alt: 'Web designers and developers creating modern website with UX/UI design in collaborative workspace',
    hoverAlt: 'Polished responsive website design showcasing modern UX/UI layout and on-page SEO structure'
  },
  {
    id: 7,
    name: 'Custom Consulting & Add-Ons – Tailored Services On Demand',
    nameDe: 'Custom Consulting & Add-Ons – Maßgeschneiderte Services auf Abruf',
    description: 'Flexible support for projects beyond our standard packages, including tailored problem solving and workshops, AI consulting, product development, strategic planning, market and competitor research, ad creation, landing pages, conversion-rate optimisation, app store optimisation (ASO), analytics dashboards and ongoing campaign adjustments.',
    descriptionDe: 'Flexible Unterstützung für Projekte jenseits unserer Standard-Pakete, einschließlich maßgeschneiderter Problemlösung und Workshops, KI-Beratung, Produktentwicklung, strategischer Planung, Markt- und Wettbewerbsanalyse, Anzeigenerstellung, Landing Pages, Conversion-Rate-Optimierung, App-Store-Optimierung (ASO), Analytik-Dashboards und laufende Kampagnenanpassungen.',
    price: '€120 per hour',
    priceDe: '€120 pro Stunde',
    image: '/assets/images/custom_consulting_addons_meeting.png',
    hoverImage: '/assets/images/custom_consulting_addons.png',
    alt: 'Business consultants and clients collaborating on custom solutions in modern meeting room',
    hoverAlt: 'Custom consulting services overview showing tailored problem solving and strategic planning tools'
  },
  {
    id: 8,
    name: 'Wildcard Deal – Surprising Solutions For Extraordinary Challenges',
    nameDe: 'Wildcard Deal – Überraschende Lösungen für außergewöhnliche Herausforderungen',
    description: 'A bold outside the box approach for tackling unique challenges in your business. Perfect when you feel stuck or want surprising solutions that break free from conventional thinking. We will go beyond your imagination and explore innovative ideas tailored to your situation. If your business growth is blocked by inside or outside conflict we will analyze both parties and mediate towards a productive win-win outcome.',
    descriptionDe: 'Ein mutiger, unkonventioneller Ansatz zur Bewältigung einzigartiger Herausforderungen in Ihrem Unternehmen. Perfekt, wenn Sie feststecken oder überraschende Lösungen wünschen, die sich vom konventionellen Denken lösen. Wir gehen über Ihre Vorstellungskraft hinaus und erkunden innovative Ideen, die auf Ihre Situation zugeschnitten sind. Wenn Ihr Geschäftswachstum durch interne oder externe Konflikte blockiert wird, analysieren wir beide Parteien und vermitteln zu einem produktiven Win-Win-Ergebnis.',
    price: 'Contact us for individual pricing',
    priceDe: 'Kontaktieren Sie uns für individuelle Preise',
    image: '/assets/images/wildcard_deal_solutions_brainstorm.png',
    hoverImage: '/assets/images/wildcard_deal_solutions.png',
    alt: 'Creative problem-solving session with innovative thinkers brainstorming breakthrough solutions',
    hoverAlt: 'Wildcard deal unconventional solutions showcase with cross-domain expertise and breakthrough strategies'
  }
];

interface ServicesInteractiveProps {
  lang?: 'en' | 'de';
}

const ServicesInteractive = ({ lang = 'en' }: ServicesInteractiveProps) => {
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'de'>(lang);
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent<{ language: 'en' | 'de' }>) => {
      setCurrentLanguage(event.detail.language);
    };

    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, []);

  const handleServiceClick = (serviceName: string) => {
    // Store selected service in localStorage for contact form to pick up
    localStorage.setItem('selectedService', serviceName);
  };

  return (
    <section className="relative z-10 min-h-screen px-4 pt-32 pb-20">
      <div className="container mx-auto">
        <h2
          data-lang="en"
          className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-4 px-4"
          style={{
            fontFamily: '"Exo 2", sans-serif',
            color: '#00b8ff',
            display: currentLanguage === 'en' ? 'block' : 'none',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          Services
        </h2>
        <p
          data-lang="en"
          className="text-3xl font-semibold text-center mb-16 px-4"
          style={{
            fontFamily: '"Exo 2", sans-serif',
            color: '#16c55f',
            display: currentLanguage === 'en' ? 'block' : 'none',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          How we boost your business
        </p>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {servicesData.map((service) => {
            const isHovered = hoveredId === service.id;
            return (
              <div
                key={service.id}
                className="service-card p-6 rounded-lg transition-all duration-300 flex flex-col h-full"
                style={{
                  backgroundColor: 'rgba(3, 27, 58, 0.8)',
                  border: `2px solid ${isHovered ? '#16c55f' : '#00b8ff'}`,
                  boxShadow: isHovered ? '0 8px 16px rgba(22, 197, 95, 0.4)' : '0 4px 6px rgba(0, 184, 255, 0.2)',
                  transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
                  minHeight: '600px'
                }}
                onMouseEnter={() => setHoveredId(service.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Service Image with dual hover */}
                <div className="flex justify-center mb-4">
                  <div
                    className="relative w-full h-48 rounded-lg overflow-hidden"
                    style={{
                      border: '2px solid #00b8ff',
                      boxShadow: '0 0 15px rgba(0, 184, 255, 0.4)'
                    }}
                  >
                    {/* Default image - now shows on hover */}
                    <Image
                      src={service.image}
                      alt={service.alt}
                      fill
                      sizes="(max-width: 768px) 100vw, 50vw"
                      className="object-cover transition-opacity duration-500"
                      style={{ opacity: isHovered ? 1 : 0 }}
                    />
                    {/* Hover image - now shows by default */}
                    <Image
                      src={service.hoverImage}
                      alt={service.hoverAlt}
                      fill
                      sizes="(max-width: 768px) 100vw, 50vw"
                      className="object-cover transition-opacity duration-500"
                      style={{ opacity: isHovered ? 0 : 1 }}
                    />
                  </div>
                </div>

                {/* Service Name */}
                <h3
                  data-lang="en"
                  className="text-center mb-3"
                  style={{
                    fontFamily: '"Exo 2", sans-serif',
                    display: currentLanguage === 'en' ? 'block' : 'none'
                  }}
                >
                  <span className="text-2xl font-bold block" style={{ color: '#16c55f' }}>
                    {service.name.split(' – ')[0]}
                  </span>
                  <span className="text-lg font-semibold block mt-1" style={{ color: '#00b8ff' }}>
                    {service.name.split(' – ')[1]}
                  </span>
                </h3>
                <h3
                  data-lang="de"
                  className="text-center mb-3"
                  style={{
                    fontFamily: '"Exo 2", sans-serif',
                    display: currentLanguage === 'de' ? 'block' : 'none'
                  }}
                >
                  <span className="text-2xl font-bold block" style={{ color: '#16c55f' }}>
                    {service.nameDe.split(' – ')[0]}
                  </span>
                  <span className="text-lg font-semibold block mt-1" style={{ color: '#00b8ff' }}>
                    {service.nameDe.split(' – ')[1]}
                  </span>
                </h3>

                {/* Service Description */}
                <p
                  data-lang="en"
                  className="text-center mb-4"
                  style={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#ffffff',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    display: currentLanguage === 'en' ? 'block' : 'none'
                  }}
                >
                  {service.description}
                </p>
                <p
                  data-lang="de"
                  className="text-center mb-4"
                  style={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#ffffff',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    display: currentLanguage === 'de' ? 'block' : 'none'
                  }}
                >
                  {service.descriptionDe}
                </p>

                {/* Service Price */}
                <p
                  data-lang="en"
                  className="text-center font-semibold mb-4"
                  style={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#00b8ff',
                    fontSize: '1.1rem',
                    display: currentLanguage === 'en' ? 'block' : 'none'
                  }}
                >
                  {service.price}
                </p>
                <p
                  data-lang="de"
                  className="text-center font-semibold mb-4"
                  style={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#00b8ff',
                    fontSize: '1.1rem',
                    display: currentLanguage === 'de' ? 'block' : 'none'
                  }}
                >
                  {service.priceDe}
                </p>

                {/* Book Now Button */}
                <div className="flex justify-center">
                  <Link 
                    href="/contact-section"
                    onClick={() => handleServiceClick(currentLanguage === 'en' ? service.name : service.nameDe)}
                  >
                    <button
                      data-lang="en"
                      className="px-6 py-3 text-base font-semibold rounded-lg transition-all duration-300"
                      style={{
                        fontFamily: '"Exo 2", sans-serif',
                        backgroundColor: 'transparent',
                        border: '2px solid #16c55f',
                        color: '#16c55f',
                        display: currentLanguage === 'en' ? 'inline-block' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#16c55f';
                        e.currentTarget.style.color = '#031b3a';
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(22, 197, 95, 0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#16c55f';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      Book now
                    </button>
                  </Link>
                  <Link 
                    href="/contact-section"
                    onClick={() => handleServiceClick(currentLanguage === 'en' ? service.name : service.nameDe)}
                  >
                    <button
                      data-lang="de"
                      className="px-6 py-3 text-base font-semibold rounded-lg transition-all duration-300"
                      style={{
                        fontFamily: '"Exo 2", sans-serif',
                        backgroundColor: 'transparent',
                        border: '2px solid #16c55f',
                        color: '#16c55f',
                        display: currentLanguage === 'de' ? 'inline-block' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#16c55f';
                        e.currentTarget.style.color = '#031b3a';
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(22, 197, 95, 0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#16c55f';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      Jetzt buchen
                    </button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Leveraging AI & Human Intelligence Section */}
        <div className="w-full max-w-4xl mx-auto space-y-8 animate-fade-in mt-16">
          <h3 
            className="text-3xl md:text-5xl font-bold text-center mb-4" 
            style={{
              fontFamily: '"Exo 2", sans-serif',
              color: '#00b8ff'
            }}
            data-lang={currentLanguage}
          >
            {currentLanguage === 'en' ? 'Leveraging Artificial & Human Intelligence' : 'Das Beste aus Künstlicher & Menschlicher Intelligenz'}
          </h3>
          <div className="space-y-6 rounded-xl p-8 border border-[#00b8ff]/20 bg-black/40 backdrop-blur-sm">
            <div className="space-y-4 text-foreground-secondary">
              <div className="flex gap-3">
                <span className="text-[#16c55f] font-bold text-xl flex-shrink-0">•</span>
                <p className="text-lg leading-relaxed">
                  <strong className="text-[#16c55f]">{currentLanguage === 'en' ? 'AI‑First with Human Insight:' : 'KI‑First mit menschlicher Einsicht:'}</strong> {currentLanguage === 'en' ? 'We blend cutting edge AI automation with 15 years of expert marketing and business growth experience to deliver strategies that are both data driven and deeply human. You get the speed and scale of advanced models without losing human nuance, creativity or empathy.' : 'Wir verbinden modernste KI‑Automatisierung mit 15 Jahren Expertise in Marketing und Unternehmenswachstum, um Strategien zu liefern, die sowohl datengetrieben als auch zutiefst menschlich sind. Sie erhalten die Geschwindigkeit und Skalierbarkeit fortschrittlicher Modelle, ohne menschliche Nuancen, Kreativität oder Empathie zu verlieren.'}
                </p>
              </div>
              <div className="flex gap-3">
                <span className="text-[#16c55f] font-bold text-xl flex-shrink-0">•</span>
                <p className="text-lg leading-relaxed">
                  <strong className="text-[#16c55f]">{currentLanguage === 'en' ? 'Human‑in‑the‑Loop Promise:' : 'Human-in-the-Loop Garantie:'}</strong> {currentLanguage === 'en' ? 'AI never acts alone. Critical content, campaign decisions and customer interactions always pass through an expert for review. This ensures brand safety, ethical standards and the unique voice your customers expect.' : 'KI handelt nie allein. Kritische Inhalte, Kampagnenentscheidungen und Kundeninteraktionen passieren immer durch einen Experten für Überprüfung. Dies gewährleistet Markensicherheit, ethische Standards und die einzigartige Stimme, die Ihre Kunden erwarten.'}
                </p>
              </div>
              <div className="flex gap-3">
                <span className="text-[#16c55f] font-bold text-xl flex-shrink-0">•</span>
                <p className="text-lg leading-relaxed">
                  <strong className="text-[#16c55f]">{currentLanguage === 'en' ? 'Collaborative & Educational:' : 'Kollaborativ & Lehrreich:'}</strong> {currentLanguage === 'en' ? 'Our mission is to empower your team. From workshops on AI adoption to custom problem solving we teach you how to harness AI automation and interpret insights so you stay ahead long after the project ends.' : 'Unsere Mission ist es, Ihr Team zu befähigen. Von Workshops zur KI-Einführung bis hin zu maßgeschneiderter Problemlösung lehren wir Sie, wie Sie KI-Automatisierung nutzen und Erkenntnisse interpretieren, damit Sie auch lange nach Projektende vorne bleiben.'}
                </p>
              </div>
              <div className="flex gap-3">
                <span className="text-[#16c55f] font-bold text-xl flex-shrink-0">•</span>
                <p className="text-lg leading-relaxed">
                  <strong className="text-[#16c55f]">{currentLanguage === 'en' ? 'Innovation & Flexibility:' : 'Innovation & Flexibilität:'}</strong> {currentLanguage === 'en' ? 'As AI technology evolves we integrate new models and tools quickly, giving you access to the latest advances without escalating costs. We audit your existing tech stack, identify where AI creates leverage and build custom frameworks using AI and open source infrastructure. Deployed, documented and maintained.' : 'Während sich die KI‑Technologie weiterentwickelt, integrieren wir neue Modelle und Tools schnell und geben Ihnen Zugang zu den neuesten Fortschritten, ohne die Kosten zu erhöhen. Wir prüfen Ihren bestehenden Tech-Stack, identifizieren, wo KI Hebelwirkung erzeugt, und bauen maßgeschneiderte Frameworks mit KI und Open-Source-Infrastruktur. Bereitgestellt, dokumentiert und gewartet.'}
                </p>
              </div>
            </div>
          </div>

          {/* Contact Us CTA Button */}
          <div className="pt-8 text-center">
            <Link
              href={`/${currentLanguage}/contact-section`}
              prefetch={false}
              className="inline-block px-8 py-4 bg-[#00b8ff] hover:bg-[#16c55f] text-white font-semibold rounded-lg transition-colors duration-300 shadow-lg hover:shadow-xl text-lg"
              data-lang={currentLanguage}
            >
              {currentLanguage === 'en' ? 'Contact Us →' : 'Kontaktieren Sie uns →'}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesInteractive;